import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type CandidateNav =
  | 'overview'
  | 'skills'
  | 'activity'
  | 'interviews'
  | 'achievements'
  | 'settings';

interface CandidateNavContextValue {
  activeNav: CandidateNav | null;
  setCandidateNav: (nav: CandidateNav | null) => void;
}

const CandidateNavContext = createContext<CandidateNavContextValue | null>(null);

export const CandidateNavProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [activeNav, setActiveNav] = useState<CandidateNav | null>(null);
  const setCandidateNav = useCallback((nav: CandidateNav | null) => {
    setActiveNav(nav);
  }, []);

  const value = useMemo(
    () => ({ activeNav, setCandidateNav }),
    [activeNav, setCandidateNav],
  );

  return (
    <CandidateNavContext.Provider value={value}>
      {children}
    </CandidateNavContext.Provider>
  );
};

export const useCandidateNav = (): CandidateNav | null => {
  const ctx = useContext(CandidateNavContext);
  return ctx?.activeNav ?? null;
};

/** Sync CandidateProfile tab state to the app-level context for global widgets. */
export const useSyncCandidateNav = (nav: CandidateNav): void => {
  const setCandidateNav = useContext(CandidateNavContext)?.setCandidateNav;

  useEffect(() => {
    setCandidateNav?.(nav);
    return () => setCandidateNav?.(null);
  }, [nav, setCandidateNav]);
};
